package com.evoapps.evoshift.domain.usecase.shift

import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.ActualWork
import com.evoapps.evoshift.domain.model.ActualWorker
import com.evoapps.evoshift.domain.model.AuditMetadata
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.DayClosedException
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.NotificationType
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftSplitException
import com.evoapps.evoshift.domain.model.ShiftSplits
import com.evoapps.evoshift.domain.model.SplitProblem
import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import com.evoapps.evoshift.domain.usecase.NoPayRatesException
import com.evoapps.evoshift.domain.usecase.ReportAbsenceUseCase
import com.evoapps.evoshift.domain.usecase.finance.CalculateDailyRecordUseCase
import com.evoapps.evoshift.domain.util.AppClock

/**
 * SH-236: what the "who actually worked" and "split shift" actions share: the day's closing,
 * the repricing of the shifts and the day's record, and the absence a manager may record with it.
 */
class ShiftWorkFollowUps(
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase,
    private val calculateShiftCost: CalculateShiftCostUseCase? = null,
    private val absenceRepository: AbsenceRepository? = null,
    private val userRepository: UserRepository? = null
) {
    /** A closed (locked) day is final. Days whose results the caller cannot read count as open. */
    suspend fun requireOpen(rosterId: String, date: String) {
        val stored = financialRepository.getDailyRecordsForRoster(rosterId).getOrNull()?.firstOrNull { it.date == date }
        if (stored?.isLocked == true) throw DayClosedException(date)
    }

    /**
     * Prices [shiftIds] for who works them now (a financial manager's pay rates; skipped
     * otherwise). True when one of them could not be priced for want of pay rates.
     */
    suspend fun reprice(shiftIds: List<String>): Boolean = shiftIds.map { id ->
        calculateShiftCost?.invoke(id)?.exceptionOrNull().let { it is NoPayRatesException || it is NotFoundException }
    }.any { it }

    /** The day's record recomputed and saved; null when the caller may not (no money rights) or it failed. */
    suspend fun recomputeDay(workplaceId: String, rosterId: String, date: String): DailyRecord? = runCatching {
        calculateDailyRecordUseCase.recompute(workplaceId, rosterId, date) { it }.getOrThrow()
            .also { financialRepository.saveDailyRecord(it).getOrThrow() }
    }.getOrNull()

    /**
     * An approved [type] absence of [userId] on [date] (from [startTime] to [endTime], "HH:mm",
     * for the rest of a day; null: the whole day), entered by the manager [editorUserId], and the
     * person told (ReportAbsenceUseCase.notifyOnBehalf). The shift it comes from is not freed: it
     * is already recorded as not worked, or split. Returns whether it was saved.
     */
    suspend fun recordAbsence(
        userId: String, workplaceId: String, date: String, type: AbsenceType, reason: String?,
        editorUserId: String, startTime: String? = null, endTime: String? = null
    ): Boolean {
        val absences = absenceRepository ?: return false
        val now = AppClock.now().toString()
        val absence = Absence(
            id = "", userId = userId, workplaceId = workplaceId, type = type, status = AbsenceStatus.APPROVED,
            startDate = date, endDate = date,
            reason = reason?.trim()?.take(ReportAbsenceUseCase.REASON_MAX_LENGTH)?.takeIf { it.isNotEmpty() },
            attachmentUrl = null, processedBy = editorUserId, processedAt = now,
            metadata = AuditMetadata(createdAt = now, createdBy = editorUserId),
            startTime = startTime?.takeIf { endTime != null }, endTime = endTime?.takeIf { startTime != null }
        )
        val id = absences.createAbsence(absence).getOrNull() ?: return false
        userRepository?.let {
            runCatching {
                ReportAbsenceUseCase.notifyOnBehalf(it, workplaceId, userId, editorUserId, date, date, id, NotificationData.ABSENCE_ID, approved = true)
            }
        }
        return true
    }
}

/**
 * SH-236: "Who actually worked": a manager records, for a shift of a past day or of today, that it
 * was worked as planned, by someone else ([ActualWorker.Person], `actualUserId`; the planned
 * assignee stays), or that nobody came ([ActualWorker.Nobody], `notWorked` and an optional reason;
 * then it has no cost and the clock times go), with the actual times ([actual]; null keeps the
 * clocked ones, or takes the planned ones when none were clocked). The shift is repriced for who
 * worked it and the day's record recomputed, so costs, hours, the labour share and the exports
 * follow; [absenceType] also records the planned person's absence that day (a no-show only).
 */
class RecordActualWorkUseCase(
    private val shiftRepository: ShiftRepository,
    private val followUps: ShiftWorkFollowUps
) {
    data class Outcome(
        val shift: Shift,
        val record: DailyRecord?,
        val missingPayRates: Boolean = false,
        /** null: no absence asked for; else whether it was saved. */
        val absenceRecorded: Boolean? = null
    )

    suspend operator fun invoke(
        workplaceId: String,
        rosterId: String,
        shiftId: String,
        worker: ActualWorker,
        actual: TimeRange?,
        editorUserId: String,
        absenceType: AbsenceType? = null
    ): Result<Outcome> = runCatching {
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        followUps.requireOpen(rosterId, shift.date)
        val nobody = worker is ActualWorker.Nobody
        val actualUserId = when (worker) {
            ActualWorker.AsPlanned -> {
                require(shift.userId != null) { "An unassigned shift has no plan to confirm" }
                null
            }
            is ActualWorker.Person -> worker.userId.takeIf { it != shift.userId }
            is ActualWorker.Nobody -> null
        }
        val reason = (worker as? ActualWorker.Nobody)?.reason?.trim()?.take(ActualWork.MAX_NOTE)?.takeIf { it.isNotEmpty() }
        shiftRepository.recordActualWork(shiftId, actualUserId, nobody, reason, editorUserId).getOrThrow()
        // The hours: as typed, else as clocked, else as planned; nobody came: no clock times.
        val times = when {
            nobody -> null
            actual != null -> actual
            shift.hasActualTime -> shift.actualTime
            else -> shift.plannedTime
        }
        if (times != shift.actualTime && (times != null || shift.hasActualTime)) {
            shiftRepository.updateActualTime(shiftId, times, editorUserId).getOrThrow()
        }
        val updated = shift.copy(actualUserId = actualUserId, notWorked = nobody, noShowReason = reason, actualTime = times)
        val missingRates = followUps.reprice(listOf(shiftId)) && !nobody
        val absence = if (nobody && absenceType != null && shift.userId != null) {
            followUps.recordAbsence(shift.userId, workplaceId, shift.date, absenceType, reason, editorUserId)
        } else null
        val record = followUps.recomputeDay(workplaceId, rosterId, shift.date)
        Outcome(updated, record, missingRates, absence)
    }
}

/**
 * SH-236: "Split shift": [shiftId]'s person stops at [handoverAt] (fell ill, had to leave) and
 * [replacementId] takes over until the planned end ([ShiftSplits.split]). On a past day or today
 * the parts count at once; on a coming day it is a planned split ([today]), waiting for publishing
 * like any edit. Both parts are repriced and the day recomputed; the colleague is told when the
 * shift is published; with [recordSickLeave] the original person's sick leave for the rest of the
 * day is recorded (approved, the person told).
 */
class SplitShiftUseCase(
    private val shiftRepository: ShiftRepository,
    private val followUps: ShiftWorkFollowUps,
    private val userRepository: UserRepository? = null
) {
    data class Outcome(
        val first: Shift,
        val second: Shift,
        val record: DailyRecord?,
        val missingPayRates: Boolean = false,
        val absenceRecorded: Boolean? = null
    )

    suspend operator fun invoke(
        workplaceId: String,
        rosterId: String,
        shiftId: String,
        handoverAt: String,
        replacementId: String,
        reason: String?,
        sickness: Boolean,
        recordSickLeave: Boolean,
        editorUserId: String,
        today: String
    ): Result<Outcome> = runCatching {
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        try {
            followUps.requireOpen(rosterId, shift.date)
        } catch (e: DayClosedException) {
            throw ShiftSplitException(SplitProblem.DAY_CLOSED)
        }
        val replacementName = userRepository?.getUserProfile(replacementId)?.getOrNull()?.displayName
        val now = AppClock.now().toString()
        val parts = ShiftSplits.split(
            shift, handoverAt, replacementId, replacementName, reason, sickness, editorUserId, now,
            planned = shift.date > today
        )
        val newId = shiftRepository.splitShift(shift, parts.first, parts.second).getOrThrow()
        val second = parts.second.copy(id = newId)
        val missingRates = followUps.reprice(listOf(shift.id, newId))
        if (shift.isPublished) notifyReplacement(workplaceId, shift, second)
        val original = shift.workerId
        val absence = if (recordSickLeave && original != null) {
            val end = shift.plannedTime.end
            followUps.recordAbsence(
                original, workplaceId, shift.date, AbsenceType.SICK_LEAVE, parts.first.split?.reason, editorUserId,
                startTime = second.plannedTime.start.substringAfter('T').take(5),
                endTime = end.substringAfter('T').take(5)
            )
        } else null
        val record = followUps.recomputeDay(workplaceId, rosterId, shift.date)
        Outcome(parts.first, second, record, missingRates, absence)
    }

    private suspend fun notifyReplacement(workplaceId: String, original: Shift, second: Shift) {
        val users = userRepository ?: return
        val from = original.workerId?.let { users.getUserProfile(it).getOrNull()?.displayName }
        runCatching {
            users.sendNotification(
                Notification(
                    userId = second.userId ?: return,
                    title = "You are taking over a shift",
                    message = "You take over ${from ?: "a colleague"}'s shift on ${second.date} from ${second.plannedTime.start.substringAfter('T').take(5)}.",
                    type = NotificationType.SHIFT_ASSIGNED,
                    createdAt = AppClock.now().toString(),
                    data = NotificationData.forShift(
                        second, workplaceId,
                        NotificationData.KIND to NotificationData.Kind.TAKEOVER,
                        NotificationData.NAME to from,
                        NotificationData.SUBJECT_USER_ID to original.workerId
                    )
                )
            )
        }
    }
}

/**
 * SH-236: "Undo split": the two parts of a split back into the original shift
 * ([ShiftSplits.undo]); either part may be given. Repriced and the day recomputed. An absence
 * recorded with the split stays (the manager removes it in Requests if it was a mistake).
 */
class UndoShiftSplitUseCase(
    private val shiftRepository: ShiftRepository,
    private val followUps: ShiftWorkFollowUps,
    private val financialRepository: FinancialRepository? = null
) {
    data class Outcome(val restored: Shift, val removedId: String, val record: DailyRecord?)

    suspend operator fun invoke(workplaceId: String, rosterId: String, shiftId: String, editorUserId: String): Result<Outcome> = runCatching {
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        val split = shift.split ?: throw ShiftSplitException(SplitProblem.OTHER_PART_MISSING)
        try {
            followUps.requireOpen(rosterId, shift.date)
        } catch (e: DayClosedException) {
            throw ShiftSplitException(SplitProblem.DAY_CLOSED)
        }
        val month = shiftRepository.getShiftsForRoster(rosterId).getOrThrow()
        val first = if (split.isFirst) shift else month.firstOrNull { it.id == split.groupId }
            ?: throw ShiftSplitException(SplitProblem.OTHER_PART_MISSING)
        val second = ShiftSplits.otherPart(first, month) ?: throw ShiftSplitException(SplitProblem.OTHER_PART_MISSING)
        val restored = ShiftSplits.undo(first, second, editorUserId, AppClock.now().toString())
        shiftRepository.undoSplit(restored, second.id).getOrThrow()
        // The second part's snapshot goes with it (onShiftDeleted too); the first is priced whole again.
        financialRepository?.let { runCatching { it.deleteShiftFinancials(second.id).getOrThrow() } }
        followUps.reprice(listOf(first.id))
        val record = followUps.recomputeDay(workplaceId, rosterId, first.date)
        Outcome(restored, second.id, record)
    }
}
